import React, { useEffect, useState } from "react";
import EnhancedLatexRenderer from "./EnhancedLatexRenderer";
import { getApiUrl } from "../config/api";

export const MessageRendererNTUST = ({ text, container_actionLinks }) => {
    const [fileContent, setFileContent] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");
    const [youtubeUrl, setYoutubeUrl] = useState("");
    const [videoTitle, setVideoTitle] = useState("");
    const [showTranscript, setShowTranscript] = useState(false);
    const [parsedContent, setParsedContent] = useState({
        metadata: {},
        chapters: [],
        summary: "",
        language: "en", // Default to English
    });

    // Replace storage.cloud.google.com with storage.googleapis.com in the URL
    const modifiedUrl =
        container_actionLinks && container_actionLinks[0]?.url
            ? container_actionLinks[0].url.replace(
                  "https://storage.cloud.google.com/",
                  "https://storage.googleapis.com/",
              )
            : "";

    useEffect(() => {
        const fetchFileContent = async () => {
            if (!modifiedUrl) return;

            setIsLoading(true);
            setError("");

            try {
                // Remove the hash fragment from URL for fetching
                const cleanUrl = modifiedUrl.split("#")[0];

                // Use backend proxy instead of direct fetch to avoid CORS issues
                const backendUrl = getApiUrl();
                const proxyUrl = `${backendUrl}/api/video/transcript?url=${encodeURIComponent(cleanUrl)}`;

                const response = await fetch(proxyUrl);

                if (!response.ok) {
                    throw new Error(`Failed to fetch: ${response.status}`);
                }

                const content = await response.text();
                setFileContent(content);

                // Parse content structure
                parseContent(content);

                // Extract YouTube URL from content - support both English and Chinese
                const youtubeMatch =
                    content.match(
                        /Video URL:\s*(https:\/\/youtu\.?be[^\s\n]+)/,
                    ) ||
                    content.match(
                        /影片網址：\s*(https:\/\/youtu\.?be[^\s\n]+)/,
                    );
                if (youtubeMatch) {
                    setYoutubeUrl(youtubeMatch[1]);
                }

                // Extract Video Title - support both English and Chinese
                const titleMatch =
                    content.match(/Video Title:\s*(.+)/) ||
                    content.match(/影片標題：\s*(.+)/);
                if (titleMatch) {
                    setVideoTitle(titleMatch[1].trim());
                }
            } catch (err) {
                console.error("Error fetching file content:", err);
                setError("Failed to load video information");
            } finally {
                setIsLoading(false);
            }
        };

        fetchFileContent();
    }, [modifiedUrl]);

    // Parse content into structured sections (supports both English and Mandarin)
    const parseContent = (content) => {
        const parsed = {
            metadata: {},
            chapters: [],
            summary: "",
            language: "en", // Default language
        };

        // Detect language based on content
        const isChinese = /影片標題|影片網址|章節|摘要|影片摘要/.test(content);
        parsed.language = isChinese ? "zh" : "en";

        // Extract metadata - support both English and Chinese
        const titleMatch =
            content.match(/Video Title:\s*(.+)/) ||
            content.match(/影片標題：\s*(.+)/);
        const urlMatch =
            content.match(/Video URL:\s*(https:\/\/[^\s\n]+)/) ||
            content.match(/影片網址：\s*(https:\/\/[^\s\n]+)/);

        if (titleMatch) parsed.metadata.title = titleMatch[1].trim();
        if (urlMatch) parsed.metadata.url = urlMatch[1].trim();

        // Extract chapters - support both English and Chinese
        let chapterRegex;
        if (isChinese) {
            // Chinese format: 章節 1：介紹與測試準備 (00:00 - 00:10)
            chapterRegex =
                /章節\s+(\d+)：\s*([^(]+)\s*\(([^)]+)\)\s*摘要：\s*([^\n]+(?:\n(?!章節|3\.|1\.|2\.)[^\n]+)*)/g;
        } else {
            // English format: Chapter 1: Title (00:00 - 00:10) Summary: ...
            chapterRegex =
                /Chapter\s+(\d+):\s*([^(]+)\s*\(([^)]+)\)\s*Summary:\s*([^\n]+(?:\n(?!Chapter|3\.|1\.|2\.)[^\n]+)*)/g;
        }

        let chapterMatch;
        while ((chapterMatch = chapterRegex.exec(content)) !== null) {
            parsed.chapters.push({
                number: chapterMatch[1],
                title: chapterMatch[2].trim(),
                timestamp: chapterMatch[3].trim(),
                summary: chapterMatch[4].trim(),
            });
        }

        // Extract summary (section 3) - support both languages
        const summaryMatch =
            content.match(/3\.\s*Video Summary\s*\n([\s\S]+?)(?:\n\n|$)/) ||
            content.match(/3\.\s*影片摘要\s*\n([\s\S]+?)(?:\n\n|$)/);

        if (summaryMatch) {
            parsed.summary = summaryMatch[1].trim();
        }

        setParsedContent(parsed);
    };

    // Extract video ID from YouTube URL for embedding
    const getYouTubeVideoId = (url) => {
        if (!url) return null;
        const regExp =
            /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
        const match = url.match(regExp);
        return match && match[2].length === 11 ? match[2] : null;
    };

    const videoId = getYouTubeVideoId(youtubeUrl);

    return (
        <div className="message-renderer-ntust">
            <EnhancedLatexRenderer text={text} />

            {container_actionLinks && container_actionLinks[0] && (
                <div className="mt-4">
                    {isLoading && (
                        <div className="flex items-center gap-2 text-gray-400 text-sm p-3 bg-black/5 dark:bg-white/5 rounded-lg">
                            <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                            {parsedContent.language === "zh"
                                ? "載入影片資訊中..."
                                : "Loading video information..."}
                        </div>
                    )}

                    {error && (
                        <div className="text-red-400 text-sm p-3 bg-red-500/10 rounded-lg border border-red-500/30">
                            {error}
                        </div>
                    )}

                    {fileContent && !isLoading && (
                        <div className="bg-black/5 dark:bg-white/5 rounded-xl overflow-hidden border border-black/10 dark:border-white/10">
                            {/* YouTube Video Embed */}
                            {videoId && (
                                <div>
                                    {/* Video Title */}
                                    {videoTitle && (
                                        <div className="px-4 py-3 bg-black/[0.03] dark:bg-white/[0.03] border-b border-black/10 dark:border-white/10 text-lg font-semibold text-gray-800 dark:text-gray-200">
                                            {videoTitle}
                                        </div>
                                    )}

                                    {/* Video Player */}
                                    <div className="p-3">
                                        <div className="relative pb-[56.25%] h-0 overflow-hidden rounded-lg bg-black">
                                            <iframe
                                                className="absolute top-0 left-0 w-full h-full border-0"
                                                src={`https://www.youtube.com/embed/${videoId}`}
                                                title="YouTube video player"
                                                frameBorder="0"
                                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                                allowFullScreen
                                            ></iframe>
                                        </div>

                                        {/* Watch on YouTube Link */}
                                        {youtubeUrl && (
                                            <a
                                                href={youtubeUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="inline-flex items-center gap-1.5 mt-2.5 px-3 py-1.5 bg-blue-500 dark:bg-blue-500/10 text-white dark:text-blue-400 no-underline text-sm rounded-md border border-blue-500/30 transition-all hover:bg-blue-500/20 hover:border-blue-500/50"
                                            >
                                                {/* <span>🔗</span> */}
                                                <span>
                                                    {parsedContent.language ===
                                                    "zh"
                                                        ? "在 YouTube 上觀看"
                                                        : "Watch on YouTube"}
                                                </span>
                                            </a>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Collapsible Content Sections */}
                            <div>
                                <button
                                    onClick={() =>
                                        setShowTranscript(!showTranscript)
                                    }
                                    className={`w-full px-4 py-3 border-none border-t border-white/10 text-gray-800 dark:text-gray-200 text-sm font-medium cursor-pointer flex items-center justify-between transition-all hover:bg-white/[0.08] ${
                                        showTranscript
                                            ? "bg-black/[0.08] dark:bg-white/[0.08]"
                                            : "bg-black/[0.03] dark:bg-white/[0.03]"
                                    }`}
                                >
                                    <span className="flex items-center gap-2">
                                        {/* <span>📄</span> */}
                                        <span>
                                            {parsedContent.language === "zh"
                                                ? "影片詳情與章節"
                                                : "Video Details & Chapters"}
                                        </span>
                                    </span>
                                    <span
                                        className={`text-xs transition-transform ${
                                            showTranscript
                                                ? "rotate-180"
                                                : "rotate-0"
                                        }`}
                                    >
                                        ▼
                                    </span>
                                </button>

                                {showTranscript && (
                                    <div className="p-4 bg-white/20 dark:bg-black/20 max-h-[500px] overflow-y-auto custom-scrollbar">
                                        {/* Video Chapters */}
                                        {parsedContent.chapters.length > 0 && (
                                            <div className="mb-5">
                                                <h3 className="text-[15px] font-semibold text-gray-950 dark:text-gray-50 mb-3 flex items-center gap-2">
                                                    <span>🎬</span>
                                                    <span>
                                                        {parsedContent.language ===
                                                        "zh"
                                                            ? "影片章節"
                                                            : "Video Chapters"}
                                                    </span>
                                                </h3>
                                                <div className="flex flex-col gap-3">
                                                    {parsedContent.chapters.map(
                                                        (chapter, index) => (
                                                            <div
                                                                key={index}
                                                                className="bg-black/5 dark:bg-white/5 p-3 rounded-lg border border-black/10 dark:border-white/10"
                                                            >
                                                                <div className="flex items-center gap-2 mb-1.5">
                                                                    <span className="bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded text-xs font-semibold">
                                                                        {
                                                                            chapter.timestamp
                                                                        }
                                                                    </span>
                                                                    <span className="text-sm font-semibold text-gray-950 dark:text-gray-50">
                                                                        {parsedContent.language ===
                                                                        "zh"
                                                                            ? `章節 ${chapter.number}`
                                                                            : `Chapter ${chapter.number}`}
                                                                    </span>
                                                                </div>
                                                                <div className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-1.5">
                                                                    {
                                                                        chapter.title
                                                                    }
                                                                </div>
                                                                <div className="text-xs leading-relaxed text-gray-700 dark:text-gray-300">
                                                                    {
                                                                        chapter.summary
                                                                    }
                                                                </div>
                                                            </div>
                                                        ),
                                                    )}
                                                </div>
                                            </div>
                                        )}

                                        {/* Video Summary */}
                                        {parsedContent.summary && (
                                            <div>
                                                <h3 className="text-[15px] font-semibold text-gray-950 dark:text-gray-50 mb-3 flex items-center gap-2">
                                                    <span>📝</span>
                                                    <span>
                                                        {parsedContent.language ===
                                                        "zh"
                                                            ? "影片摘要"
                                                            : "Video Summary"}
                                                    </span>
                                                </h3>
                                                <div className="bg-black/5 dark:bg-white/5 p-3.5 rounded-lg border border-black/10 dark:border-white/10 text-sm leading-relaxed text-gray-700 dark:text-gray-300">
                                                    {parsedContent.summary}
                                                </div>
                                            </div>
                                        )}

                                        {/* Fallback to raw content if parsing failed */}
                                        {parsedContent.chapters.length === 0 &&
                                            !parsedContent.summary && (
                                                <div className="text-sm leading-relaxed text-gray-700 dark:text-gray-300 whitespace-pre-wrap font-mono">
                                                    {fileContent}
                                                </div>
                                            )}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            )}

            <style>{`
                .custom-scrollbar::-webkit-scrollbar {
                    width: 8px;
                }
                
                .custom-scrollbar::-webkit-scrollbar-track {
                    background: rgba(255, 255, 255, 0.05);
                    border-radius: 4px;
                }
                
                .custom-scrollbar::-webkit-scrollbar-thumb {
                    background: rgba(255, 255, 255, 0.2);
                    border-radius: 4px;
                }
                
                .custom-scrollbar::-webkit-scrollbar-thumb:hover {
                    background: rgba(255, 255, 255, 0.3);
                }
            `}</style>
        </div>
    );
};
